import { sva } from '@allenai/varnish-panda-runtime/css';
import type { RecipeVariantProps } from '@allenai/varnish-panda-runtime/types';

/**
 *  On the surface, building a custom styled button seems simple. However, there are many cross
 *  browser inconsistencies in interactions and accessibility features to consider. Button handles
 *  all of these interactions for you, so you can focus on the styling.
 *
 *  See: https://react-spectrum.adobe.com/react-aria/Button.html
 */

/**
 * Button component allows users to perform actions and make selections with a single tap.
 * It supports different visual styles based on its variants: text (default), contained, and outlined.
 *
 * @variant variant Defines the visual appearance of the button.
 * - `text`: Default style with no background, minimal padding.
 * - `contained`: Button with a solid background and padding.
 * - `outlined`: Button with a border and no background.
 *
 * @variant size Defines the size of the button.
 * - `small`: Smaller button for compact UI areas.
 * - `medium`: Default size for the button.
 * - `large`: Larger button for increased touch targets.
 *
 * @variant color Controls the color scheme of the button.
 * - `primary`: Button styled with the primary color.
 * - `secondary`: Button styled with the secondary color.
 * - `tertiary`: Button styled with a less prominent tertiary color.
 */

const buttonRecipe = sva({
    slots: ['root', 'startIcon', 'endIcon'],
    base: {
        root: {
            boxSizing: 'border-box',
            // button has a font, so we need to ensure this is set
            // for the most part, manrope should be default font though (cascade down)
            fontFamily: 'body',
            gap: '[5px]',
            textDecoration: 'none',
            textAlign: 'center',

            // Override HTML button base styles
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            border: '1px solid transparent',

            cursor: 'pointer', // should should probably only apply to interactive buttons ...

            // Defaults apply to all variants
            fontWeight: 'medium',
            borderRadius: 'sm',

            // TODO: should state be a variant if/when possible
            _disabled: {
                cursor: 'not-allowed',
            },
            transition: '[background-color 250ms, color 250ms]',
        },
        startIcon: {
            verticalAlign: 'middle',
            display: 'flex',
        },
        endIcon: {
            verticalAlign: 'middle',
            display: 'flex',
        },
    },

    // Variants
    variants: {
        color: {
            default: {
                root: {
                    colorPalette: 'elements.default',
                },
            },
            primary: {
                root: {
                    colorPalette: 'elements.primary',
                },
            },
            secondary: {
                root: {
                    colorPalette: 'elements.secondary',
                },
            },
            tertiary: {
                root: {
                    colorPalette: 'elements.tertiary',
                },
            },
            warning: {
                root: {
                    colorPalette: 'elements.warning',
                },
            },
            error: {
                root: {
                    colorPalette: 'elements.error',
                },
            },
            info: {
                root: {
                    colorPalette: 'elements.information',
                },
            },
            confirmation: {
                root: {
                    colorPalette: 'elements.confirmation',
                },
            },
        },
        // Visual variant of buttons
        variant: {
            text: {
                root: {
                    color: 'colorPalette.fill',
                    _hover: {
                        _notDisabled: {
                            backgroundColor: 'elements.overrides.button.text.hovered.fill',
                        },
                    },
                    _disabled: {
                        color: 'elements.disabled.contrast-reversed',
                    },
                },
            },
            outlined: {
                root: {
                    color: 'colorPalette.fill',
                    borderColor: 'colorPalette.stroke',
                    _hover: {
                        _notDisabled: {
                            borderColor: 'colorPalette.hovered.stroke',
                        },
                    },
                    _disabled: {
                        color: 'elements.disabled.contrast-reversed',
                        borderColor: 'elements.disabled.stroke',
                    },
                },
            },
            contained: {
                root: {
                    color: 'colorPalette.contrast',
                    backgroundColor: 'colorPalette.fill',
                    _hover: {
                        _notDisabled: {
                            backgroundColor: 'colorPalette.hovered.fill',
                        },
                    },
                    _disabled: {
                        color: 'elements.disabled.contrast',
                        backgroundColor: 'elements.disabled.fill',
                    },
                },
            },
        },
        size: {
            // All sizes are assumed to have a one px border
            // for filled and text, its transparent
            small: {
                root: {
                    padding: '[7px 15px]', // values are `X` - 1px for border
                    fontSize: 'sm',
                    lineHeight: '[1rem]',
                },
                startIcon: {
                    fontSize: 'icon.xs',
                },
                endIcon: {
                    fontSize: 'icon.xs',
                },
            },
            medium: {
                root: {
                    padding: '[7px 19px]', // values are `X` - 1px for border
                    fontSize: 'sm',
                    lineHeight: '[1.25rem]',
                },
                startIcon: {
                    fontSize: '[20px]',
                },
                endIcon: {
                    fontSize: '[20px]',
                },
            },
            large: {
                root: {
                    padding: '[15px 23px]', // values are `X` - 1px for border
                    fontSize: 'md',
                    lineHeight: '[1.5rem]',
                },
                startIcon: {
                    fontSize: 'icon.sm',
                },
                endIcon: {
                    fontSize: 'icon.sm',
                },
            },
        },
        // Variants below are for icon button
        iconOnly: {
            true: {},
        },
        shape: {
            rounded: {
                root: {
                    borderRadius: 'full',
                },
            },
            box: {}, // Box by default
        },
    },
    compoundVariants: [
        // these are custom, because they have `contrast-reversed` vs just `contrast`
        {
            variant: 'outlined',
            // color: ['default', 'reversed'], // No token group for `reversed`
            color: 'default',
            css: {
                root: {
                    color: 'colorPalette.contrast-reversed',
                },
            },
        },
        // All sizes are assumed to have a one px border
        // for filled and text, its transparent
        {
            iconOnly: true,
            size: 'small',
            css: {
                root: {
                    fontSize: 'icon.sm',
                    padding: '[3px]', // values are `X` - 1px for border
                },
            },
        },
        {
            iconOnly: true,
            size: 'medium',
            css: {
                root: {
                    fontSize: 'icon.md',
                    padding: '[3px]', // values are `X` - 1px for border
                },
            },
        },
        {
            iconOnly: true,
            size: 'large',
            css: {
                root: {
                    fontSize: 'icon.lg',
                    padding: '[7px]', // values are `X` - 1px for border
                },
            },
        },
    ],
    defaultVariants: {
        variant: 'outlined',
        size: 'medium',
        color: 'default',
    },
});

type ButtonRecipeProps = RecipeVariantProps<typeof buttonRecipe>;

export { buttonRecipe, buttonRecipe as default };
export type { ButtonRecipeProps };
